import _ from 'lodash';
import { useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { setSelfUserPassword } from 'entities/Auth/sdk';

import SuccessBox from 'components/SuccessBox';
import LanguageSelector from 'components/LanguageSelector';
import { PasswordInput } from 'components/PasswordInput';
import { Button } from 'components/ui/button.tsx';

const schema = (
  errors:
    | {
        passwordRequiredField: string;
        passwordMinLength: string;
        passwordConfirmationRequiredField: string;
        passwordRules: string;
        passwordsDoNotMatch: string;
      }
    | string
) =>
  yup.object().shape({
    password: yup
      .string()
      .required(_.get(errors, 'passwordRequiredField', 'Password is required'))
      .min(8, _.get(errors, 'passwordMinLength', 'Password must be at least 8 characters'))
      .matches(
        new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\p{S}|\p{P}])/u, 'gu'),
        _.get(
          errors,
          'passwordRules',
          'Must contain at least 1 lowercase letter, 1 uppercase letter, 1 digit and 1 special symbol.'
        )
      ),
    passwordConfirmation: yup
      .string()
      .required(_.get(errors, 'passwordConfirmationRequiredField', 'Password confirmation is required'))
      .oneOf([yup.ref('password')], _.get(errors, 'passwordsDoNotMatch', 'Passwords do not match, please try again.')),
  });

interface ISetPasswordForm {
  password: string;
  passwordConfirmation: string;
}

const SetPasswordForm = ({ email, token }: { email: string; token: string }) => {
  const { t } = useTranslation();
  const [success, setSuccess] = useState<boolean>(false);
  const [formErrors, setErrors] = useState({ errors: {}, message: '' });

  const {
    handleSubmit,
    formState: { isValid, isSubmitted },
    control,
    reset,
  } = useForm<ISetPasswordForm>({
    defaultValues: { password: '', passwordConfirmation: '' },
    mode: 'onBlur',
    resolver: yupResolver(schema(t('setPassword.errors'))),
  });

  const onSubmit: SubmitHandler<ISetPasswordForm> = ({ password, passwordConfirmation }) => {
    setErrors({ errors: {}, message: '' });
    setSelfUserPassword({ token, email, password, passwordConfirmation, locale: i18n.language ?? 'en' })
      .then(() => setSuccess(true))
      .catch((error) => {
        error.response?.text()?.then((data: string) => setErrors(JSON.parse(data)));
        reset({ password: '', passwordConfirmation: '' });
      });
  };

  return (
    <>
      {success && <SuccessBox text={t('setPassword.successTitle')} />}
      {!success && (
        <>
          <div className='w-full pb-[24px] flex justify-between items-center'>
            <h1 className='bold text-grey800 font-[DKLiquidEmbrace] text-3xl text-center md:text-left'>
              {t('setPassword.title')}
            </h1>
            <LanguageSelector
              hoverStyle='bg-brand-yellow hover:bg-brand-yellow data-[popup-open]:bg-brand-yellow/60'
              mobileStyle='text-brand-brown'
            />
          </div>

          {!_.isEmpty(formErrors.errors) && (
            <div className='text-danger pb-[10px]'> {_.map(formErrors?.errors, (value: string) => value)}</div>
          )}
          {_.isEmpty(formErrors.errors) && formErrors.message !== '' && (
            <div className='text-danger pb-[10px]'>{formErrors.message}</div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-col gap-[24px]'}>
            <Controller
              control={control}
              name='password'
              render={({ field, fieldState: { error, isTouched } }) => (
                <PasswordInput
                  label={t('setPassword.createPasswordLabel')}
                  placeholder={t('setPassword.createPasswordPlaceholder')}
                  error={error}
                  isTouched={isTouched}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name='passwordConfirmation'
              render={({ field, fieldState: { error, isTouched } }) => (
                <PasswordInput
                  label={t('setPassword.confirmPasswordLabel')}
                  placeholder={t('setPassword.confirmPasswordPlaceholder')}
                  error={error}
                  isTouched={isTouched}
                  {...field}
                />
              )}
            />
            <Button type='submit' className={'w-full md:w-fit font-semibold'} disabled={!isValid || isSubmitted}>
              {t('common.confirm')}
            </Button>
          </form>
        </>
      )}
    </>
  );
};

export default SetPasswordForm;
